/** @jsxImportSource @emotion/react */
import { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { GlobalBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { useLoading } from 'components/process/Loading';
import { useMessageBar } from 'hooks/useMessageBar';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import { tb } from 'components/layouts/Table';
import _ from 'lodash';
import { useCommonModal } from 'hooks/useCommonModal';
import { DefaultProps } from '../MdIvesCmpuRegPage';
import CustomRadio from 'components/inputs/CustomRadio';
import {
  findMdIvesArticleList,
  findMdIvesMdlInfoList,
  saveMdIvesArticleList,
} from 'apis/md/MdProject';
import { MdIvesArticle, MdModelSpecInfo } from 'models/md/MdIves';
import ToggleButton from 'components/buttons/ToggleButton';
import { useNavigate } from 'react-router-dom';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { CrudCode } from 'models/common/Edit';
import * as XLSX from 'xlsx-js-style';
import dayjs from 'dayjs';

const IvesResultPage = forwardRef(
  ({ elmPrjId, isEditable = true }: DefaultProps, ref: React.Ref<any>) => {
    const { t } = useTranslation();
    const { isLoading, openLoading } = useLoading();
    const { openMessageBar } = useMessageBar();
    const { openCommonModal } = useCommonModal();
    const navigate = useNavigate();

    const [headerToggle, setHeaderToggle] = useState([true, true]);
    const [modelList, setModelList] = useState<MdModelSpecInfo[]>([]);
    const [selectedModel, setSelectedModel] = useState<MdModelSpecInfo>({});
    const [nestedRowData, setNestedRowData] = useState<any>([]);
    const [duplNestedRowData, setDuplNestedRowData] = useState<any>([]);

    const layoutDefinition = [
      { binding: 'prdnProcCdNm', header: t('md.label.공정', '공정'), width: 160 },
      { binding: 'eqclNm', header: t('md.label.설비군', '설비군'), width: 200 },
      { binding: 'ivesAtcNm', header: t('md.label.투자항목', '투자항목'), width: '*' },
      { binding: 'ivesDtlDesc', header: t('md.label.투자상세', '투자상세'), width: '*' },
      { binding: 'eqpCnt', header: t('md.label.수량', '수량'), width: 120 },
      { binding: 'quotUprc', header: t('md.label.단가', '단가'), width: 120 },
      { binding: 'ivesCost', header: t('md.label.투자비', '투자비'), width: 160 },
      { binding: 'rmk', header: t('md.label.비고', '비고'), width: '*' },
    ];

    const flattedData: MdIvesArticle[] = useMemo(() => {
      return _.flattenDeep(nestedRowData.map((proc) => proc.eqclGroups.map((eqcl) => eqcl.items)));
    }, [nestedRowData]);

    const initCodes = async () => {
      const MODEL_LIST = await findMdIvesMdlInfoList({ elmPrjId });
      setModelList(MODEL_LIST);
      if (MODEL_LIST.length > 0) {
        const selected = MODEL_LIST.find((o) => o.cfmYn === 'Y') ?? MODEL_LIST[0];
        setSelectedModel(selected);
        getData(selected.ivesMdlId);
      }
    };

    const isModify = () => {
      return !!selectedModel?.ivesMdlId && !selectedModel.ivesMdlId.includes('_NEW_');
    };

    const getData = (ivesMdlId) => {
      if (!elmPrjId || !ivesMdlId) {
        return;
      }
      // 상단 Radio 버튼 List 조회
      openLoading(true);
      // 상단 Radio 버튼별 데이터 조회
      findMdIvesArticleList({ elmPrjId, ivesMdlId })
        .then((res) => {
          const nestedObj = res.reduce((acc, item: MdIvesArticle) => {
            const prdnProcCd = item.prdnProcCd || 'UNDEFINED';
            const eqclId = item.eqclId || 'UNDEFINED';

            // 1. 공정(Process) 레벨 초기화
            if (!acc[prdnProcCd]) {
              acc[prdnProcCd] = {
                depth: 1,
                isShow: true,
                prdnProcCd,
                prdnProcCdNm: item.prdnProcCdNm,
                eqclGroups: {},
              };
            }

            // 2. 설비군(Equipment Group) 레벨 초기화
            if (!acc[prdnProcCd].eqclGroups[eqclId]) {
              acc[prdnProcCd].eqclGroups[eqclId] = {
                depth: 2,
                isShow: true,
                prdnProcCd,
                prdnProcCdNm: item.prdnProcCdNm,
                eqclId,
                eqclNm: item.eqclNm,
                items: [],
              };
            }

            // 3. 투자항목 데이터 삽입
            acc[prdnProcCd].eqclGroups[eqclId].items.push(item);

            return acc;
          }, {});

          const result = Object.values(nestedObj).map((proc: any) => {
            return {
              ...proc,
              eqclGroups: Object.values(proc.eqclGroups).map((group: any) => {
                return {
                  ...group,
                  items: group.items.map((o) => ({ ...o, depth: 3 })),
                };
              }),
            };
          });
          setNestedRowData(_.cloneDeep(result));
          setDuplNestedRowData(_.cloneDeep(result));
        })
        .finally(() => openLoading(false));
    };

    const getIvesCost = (eqpCnt, quotUprc) => {
      return (Number(eqpCnt || 0) * Number(quotUprc || 0)).toFixed(1);
    };

    const btnList = () => {
      navigate('/md/ives-cmpu');
    };

    const btnDownloadExcel = () => {
      const result = flattedData.reduce((acc: any, current, index, array) => {
        // 1. 현재 아이템을 먼저 결과 배열에 추가
        acc.push({ ...current, ivesCost: getIvesCost(current.eqpCnt, current.quotUprc) });

        // 2. 다음 아이템이 없거나(마지막 요소), 다음 아이템의 카테고리가 현재와 다르면 합계 추가
        const next = array[index + 1];
        if (!next || next.prdnProcCd !== current.prdnProcCd) {
          acc.push({
            prdnProcCd: current.prdnProcCd,
            prdnProcCdNm: current.prdnProcCdNm + ' 합계',
            ivesCost: getTotalIvesCost(current.prdnProcCd || ''),
          });
        }

        return acc;
      }, []);

      // 총 합계 추가
      result.push({
        prdnProcCd: '',
        prdnProcCdNm: '총 합계',
        ivesCost: getAllTotalCost(),
      });

      const header = {};
      layoutDefinition.forEach((field) => {
        header[field.binding] = field.header;
      });

      const rowData = [
        Object.values(header),
        ...result.map((item) => [
          item.prdnProcCdNm,
          item.eqclNm,
          item.ivesAtcNm,
          item.ivesDtlDesc,
          item.eqpCnt,
          item.quotUprc,
          Number(item.ivesCost),
          item.rmk,
        ]),
      ];

      // 워크시트 생성 (JSON -> 워크시트 변환)
      const ws = XLSX.utils.aoa_to_sheet(rowData);

      const headerStyle = {
        font: { bold: true },
        fill: { fgColor: { rgb: 'F1F4F3' } },
        alignment: { horizontal: 'center' },
      };

      // 헤더 스타일 적용 (0행 전체)
      const ref = ws['!ref'];
      if (ref) {
        const range = XLSX.utils.decode_range(ref);
        for (let c = range.s.c; c <= range.e.c; c++) {
          const headerAddr = XLSX.utils.encode_cell({ r: 0, c });
          if (!ws[headerAddr]) continue;
          ws[headerAddr].s = headerStyle;
        }

        // 행 순회하며 "합계" 텍스트 포함 여부 확인
        for (let r = range.s.r; r <= range.e.r; r++) {
          // 합계 행인지 확인 (B열의 값이 "합계"인 경우)
          const cellAddr = XLSX.utils.encode_cell({ r: r, c: 0 });
          const cell = ws[cellAddr];

          if (cell && cell.v.includes('총 합계')) {
            // 해당 행의 모든 열(A~C열)에 스타일 적용
            for (let c = range.s.c; c <= range.e.c; c++) {
              const addr = XLSX.utils.encode_cell({ r: r, c: c });
              if (!ws[addr]) ws[addr] = { t: 's', v: '' };
              ws[addr].s = {
                font: { bold: true },
                fill: { fgColor: { rgb: 'ede9fe' } },
              };
            }
          } else if (cell && cell.v.includes('합계')) {
            // 해당 행의 모든 열(A~C열)에 스타일 적용
            for (let c = range.s.c; c <= range.e.c; c++) {
              const addr = XLSX.utils.encode_cell({ r: r, c: c });
              if (!ws[addr]) ws[addr] = { t: 's', v: '' };
              ws[addr].s = {
                font: { bold: true },
                fill: { fgColor: { rgb: 'fef9c3' } },
              };
            }
          }
        }
      } else {
        // 시트에 데이터가 없습니다.
      }

      // 컬럼 너비 조정
      ws['!cols'] = layoutDefinition.map((o) => ({
        wch: o.width === '*' ? (o.binding === 'ivesDtlDesc' ? 70 : 40) : Number(o.width) / 10,
      }));

      // 빈 워크북 생성
      const wb = XLSX.utils.book_new();

      // 워크북에 워크시트 추가 ("Sheet1" 이름 지정)
      XLSX.utils.book_append_sheet(wb, ws, elmPrjId + '_' + selectedModel.ivesMdlId);

      const fileNm =
        String(t('md.label.형개발 투자비 산출 결과', '형개발 투자비 산출 결과')) +
        '_' +
        dayjs().format('YYYYMMDDHHmmss');

      // 엑셀 파일 생성 및 다운로드
      XLSX.writeFile(wb, fileNm + '.xlsx');
    };

    const btnSave = () => {
      openLoading(true);
      saveMdIvesArticleList(flattedData)
        .then((res) => {
          if (res.successOrNot === 'Y') {
            openMessageBar({
              type: 'confirm',
              content: t('com.msg.저장 되었습니다.', '저장 되었습니다.'),
            });
            setDuplNestedRowData(_.cloneDeep(nestedRowData));
          } else {
            openMessageBar({
              type: 'error',
              content: t('com.msg.저장에 실패하였습니다.', '저장에 실패하였습니다.'),
            });
          }
        })
        .catch((err) => {
          openMessageBar({
            type: 'error',
            content: t('com.msg.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
          });
        })
        .finally(() => openLoading(false));
    };

    const onToggleAllRow = (idx: number) => {
      const depth = idx + 1;
      const value = headerToggle[idx];
      setHeaderToggle((prev) => {
        const next = [...prev];
        next[idx] = !value;
        return next;
      });
      if (depth === 1) {
        setNestedRowData((prev) =>
          prev.map((proc) => {
            return { ...proc, isShow: !value };
          })
        );
      } else {
        setNestedRowData((prev) =>
          prev.map((proc) => {
            return {
              ...proc,
              eqclGroups: proc.eqclGroups.map((eqcl) => {
                return { ...eqcl, isShow: !value };
              }),
            };
          })
        );
      }
    };

    const onToggleRow = (item) => {
      const depth = item.depth;
      if (depth === 1) {
        setNestedRowData((prev) =>
          prev.map((proc) => {
            if (proc.prdnProcCd === item.prdnProcCd) return { ...proc, isShow: !proc.isShow };
            else return proc;
          })
        );
      } else {
        setNestedRowData((prev) =>
          prev.map((proc) => {
            if (proc.prdnProcCd === item.prdnProcCd)
              return {
                ...proc,
                eqclGroups: proc.eqclGroups.map((eqcl) => {
                  if (eqcl.eqclId === item.eqclId) return { ...eqcl, isShow: !eqcl.isShow };
                  else return eqcl;
                }),
              };
            else return proc;
          })
        );
      }
    };

    const btnChgModel = (ivesMdlId) => {
      const selected = modelList.find((o) => o.ivesMdlId === ivesMdlId);
      setSelectedModel({ ...selected });

      // 모델별 투자비 산출 결과 조회
      getData(ivesMdlId);
    };

    const handleOnChange = (item: any, name: string, value: any) => {
      setNestedRowData((prev) =>
        prev.map((proc) => {
          if (proc.prdnProcCd === item.prdnProcCd) {
            return {
              ...proc,
              eqclGroups: proc.eqclGroups.map((eqcl) => {
                if (eqcl.eqclId === item.eqclId) {
                  return {
                    ...eqcl,
                    items: eqcl.items.map((o) => {
                      if (o.mdlIvesAtcId === item.mdlIvesAtcId) {
                        return { ...o, [name]: value, crudKey: CrudCode.UPDATE };
                      } else return o;
                    }),
                  };
                } else return eqcl;
              }),
            };
          } else return proc;
        })
      );
    };

    const getTotalIvesCost = (prdn: string, eqcl?: string) => {
      let sumList = flattedData.filter((o) => o.prdnProcCd === prdn);
      if (eqcl) {
        sumList = sumList.filter((o) => o.eqclId === eqcl);
      }
      return _.sumBy(sumList, (o) => Number(o.eqpCnt) * Number(o.quotUprc)).toFixed(1);
    };

    const getAllTotalCost = () => {
      return _.sumBy(flattedData, (o) => Number(o.eqpCnt) * Number(o.quotUprc)).toFixed(1);
    };

    const isDirty = () => {
      const duplFlatted: MdIvesArticle[] = _.flattenDeep(
        duplNestedRowData.map((proc) => proc.eqclGroups.map((eqcl) => eqcl.items))
      );
      return !_.isEqual(
        flattedData.map((o) => o.rmk ?? ''),
        duplFlatted.map((o) => o.rmk ?? '')
      );
    };

    useImperativeHandle(ref, () => ({
      isDirty,
    }));

    useEffect(() => {
      initCodes();
    }, []);

    return (
      <>
        <SubTitleLayout>
          <div>
            {modelList.map((o, i) => {
              return (
                <CustomRadio
                  key={i}
                  id={`ivesResult-radio${i}`}
                  label={o.mdlNm || ''}
                  name="version"
                  checked={o.ivesMdlId === selectedModel?.ivesMdlId}
                  onChange={(e) => btnChgModel(o.ivesMdlId)}
                  deleteDisabled={true}
                />
              );
            })}
          </div>
          <GlobalBtnGroup>
            <Button css={IconButton.button} className="icoNone" disableRipple onClick={btnList}>
              {t('com.button.목록', '목록')}
            </Button>
            {isEditable && (
              <>
                <Button
                  css={IconButton.button}
                  className="icoNone"
                  disableRipple
                  onClick={btnDownloadExcel}
                >
                  {t('com.button.다운로드', '화면 다운로드')}
                </Button>
                <Button
                  css={IconButton.button}
                  className="save"
                  disabled={!isModify() || isLoading}
                  onClick={btnSave}
                >
                  {t('com.label.저장', '저장')}
                </Button>
              </>
            )}
          </GlobalBtnGroup>
        </SubTitleLayout>
        <SubTitleLayout>
          <SubTitleGroup>
            <h3>{t('md.label.형개발 투자비 산출 결과', '형개발 투자비 산출 결과')}</h3>
            <span className="total">
              {t('com.label.총', '총')}
              <span>{(flattedData || []).length.toLocaleString()}</span>
              {t('com.label.건', '건')}
            </span>
          </SubTitleGroup>
          <GlobalBtnGroup>
            <label className="text-sm mr-2">{t('md.label.단가(억원)', '단가(억원)')}</label>
          </GlobalBtnGroup>
        </SubTitleLayout>
        <TableContainer css={tb.tableCol}>
          <Table>
            <TableHead>
              <TableRow>
                {layoutDefinition.map((item, idx) => {
                  return (
                    <TableCell key={idx} width={item.width}>
                      {idx < 2 ? (
                        <ToggleButton
                          text={item.header}
                          initialOpen={headerToggle[idx]}
                          onToggle={() => onToggleAllRow(idx)}
                        />
                      ) : (
                        item.header
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {nestedRowData.map((proc, idx) => {
                return (
                  <>
                    <TableRow className="bg-cyan-100">
                      <TableCell>
                        <ToggleButton
                          text={proc.prdnProcCdNm}
                          initialOpen={proc.isShow}
                          onToggle={() => onToggleRow(proc)}
                        />
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell className="right !font-bold">
                        {getTotalIvesCost(proc.prdnProcCd)}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                    {proc.isShow &&
                      proc.eqclGroups.map((eqcl, idx2) => {
                        return (
                          <>
                            <TableRow key={idx2} className="bg-violet-100">
                              <TableCell></TableCell>
                              <TableCell>
                                <div className="flex justify-between items-center">
                                  <ToggleButton
                                    text={eqcl.eqclNm}
                                    initialOpen={eqcl.isShow}
                                    onToggle={() => onToggleRow(eqcl)}
                                  />
                                </div>
                              </TableCell>
                              <TableCell></TableCell>
                              <TableCell></TableCell>
                              <TableCell></TableCell>
                              <TableCell></TableCell>
                              <TableCell className="right !font-bold">
                                {getTotalIvesCost(proc.prdnProcCd, eqcl.eqclId)}
                              </TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                            {eqcl.isShow &&
                              eqcl.items.map((item, idx3) => {
                                return (
                                  <TableRow key={idx3}>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell className="left">{item.ivesAtcNm}</TableCell>
                                    <TableCell className="left">{item.ivesDtlDesc}</TableCell>
                                    <TableCell className="right">{item.eqpCnt}</TableCell>
                                    <TableCell className="right">{item.quotUprc}</TableCell>
                                    <TableCell className="right">
                                      {getIvesCost(item.eqpCnt, item.quotUprc)}
                                    </TableCell>
                                    <TableCell className="left">
                                      {isEditable ? (
                                        <CustomInputWithSearch
                                          name="rmk"
                                          value={item.rmk}
                                          onChange={(e) =>
                                            handleOnChange(item, 'rmk', e.target.value)
                                          }
                                        />
                                      ) : (
                                        <span>{item.rmk}</span>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                          </>
                        );
                      })}
                  </>
                );
              })}
              <TableRow className="bg-yellow-100">
                <TableCell className="left">{'총 합계'}</TableCell>
                <TableCell className="right"></TableCell>
                <TableCell className="right"></TableCell>
                <TableCell className="right"></TableCell>
                <TableCell className="right"></TableCell>
                <TableCell className="right"></TableCell>
                <TableCell className="right !font-bold">{getAllTotalCost()}</TableCell>
                <TableCell className="right"></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  }
);

IvesResultPage.displayName = 'IvesResultPage';

export default IvesResultPage;
